import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { translations } from '../data/translations';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Detect browser language
const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  // Check if browser language starts with 'fr'
  if (browserLang && browserLang.toLowerCase().startsWith('fr')) {
    return 'fr';
  }
  // Check if browser language starts with 'en'
  if (browserLang && browserLang.toLowerCase().startsWith('en')) {
    return 'en';
  }
  // Default to French for Quebec
  return 'fr';
};

// Get language from URL path
const getLanguageFromPath = (pathname) => {
  if (pathname.startsWith('/en')) return 'en';
  if (pathname.startsWith('/fr')) return 'fr';
  return null;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('fr');
  const [initialized, setInitialized] = useState(false);

  // Initialize language on mount
  useEffect(() => {
    const savedLang = localStorage.getItem('mtl-taxi-lang');
    if (savedLang) {
      setLanguage(savedLang);
    } else {
      // First visit - detect browser language
      const detectedLang = detectBrowserLanguage();
      setLanguage(detectedLang);
      localStorage.setItem('mtl-taxi-lang', detectedLang);
    }
    setInitialized(true);
  }, []);

  // Update localStorage and html lang when language changes
  useEffect(() => {
    if (initialized) {
      localStorage.setItem('mtl-taxi-lang', language);
      document.documentElement.lang = language;
    }
  }, [language, initialized]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'fr' ? 'en' : 'fr');
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  // Get localized path
  const getLocalizedPath = (basePath) => {
    if (basePath === '/') return `/${language}`;
    return `/${language}${basePath}`;
  };

  // Route mappings for bilingual URLs
  const routeMappings = {
    fr: {
      '/': '/fr',
      '/airport': '/fr/aeroport-yul',
      '/services': '/fr/services',
      '/fares': '/fr/tarifs',
      '/areas': '/fr/zones-desservies',
      '/faq': '/fr/faq',
      '/contact': '/fr/contact',
      '/privacy': '/fr/confidentialite',
      '/terms': '/fr/conditions-utilisation',
      '/disclaimer': '/fr/avertissement',
    },
    en: {
      '/': '/en',
      '/airport': '/en/yul-airport',
      '/services': '/en/services',
      '/fares': '/en/fares',
      '/areas': '/en/service-areas',
      '/faq': '/en/faq',
      '/contact': '/en/contact',
      '/privacy': '/en/privacy-policy',
      '/terms': '/en/terms-of-use',
      '/disclaimer': '/en/disclaimer',
    },
  };

  // Get route for current language
  const getRoute = (baseRoute) => {
    return routeMappings[language][baseRoute] || `/${language}${baseRoute}`;
  };

  // Get alternate language URL for hreflang
  const getAlternateUrl = (currentPath) => {
    const altLang = language === 'fr' ? 'en' : 'fr';
    
    // Find the base route from current path
    for (const [base, localized] of Object.entries(routeMappings[language])) {
      if (localized === currentPath || currentPath === localized) {
        return routeMappings[altLang][base];
      }
    }
    
    // Fallback: just switch language prefix
    if (currentPath.startsWith('/fr')) {
      return currentPath.replace('/fr', '/en');
    }
    if (currentPath.startsWith('/en')) {
      return currentPath.replace('/en', '/fr');
    }
    return `/${altLang}`;
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      toggleLanguage, 
      t, 
      getRoute, 
      getAlternateUrl,
      getLocalizedPath,
      initialized 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};
